import { can } from "modules/settings/accessRoles";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import PropTypes from "prop-types";
import { connect, useDispatch } from "react-redux";
import {
    Box,
    Button,
    Card,
    CardContent,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import SaveIcon from "@mui/icons-material/Save";
import EventIcon from "@mui/icons-material/Event";

import { getEffectiveBaseCharge } from "../../../setup/helpers";
import { setSnackbar } from "../../snackbar/api/snackbar";
import {
    createVendorBaseCharge,
    updateVendorBaseCharge,
    deleteVendorBaseCharge,
    getVendorBaseChargesByVendorUuid,
} from "../api/actions";

const PREVIEW_KWH = 500;
const today = () => moment().format("YYYY-MM-DD");

function formatDate(value) {
    if (!value) return "—";
    return moment(value).isValid() ? moment(value).format("YYYY-MM-DD") : value;
}

function emptyForm() {
    return {
        customerCharge: "",
        energyChargeRate: "",
        startDate: today(),
    };
}

function rowToForm(row) {
    if (!row) {
        return emptyForm();
    }
    return {
        customerCharge: row.customerCharge ?? "",
        energyChargeRate: row.energyChargeRate ?? "",
        startDate: formatDate(row.startDate) === "—" ? today() : formatDate(row.startDate),
    };
}

function computeBaseTotal(customerCharge, energyChargeRate, kwh) {
    const customer = Number(customerCharge) || 0;
    const rate = Number(energyChargeRate) || 0;
    return customer + rate * kwh;
}

function bothChargesAreZero(customerCharge, energyChargeRate) {
    return (Number(customerCharge) || 0) === 0 && (Number(energyChargeRate) || 0) === 0;
}

function VendorBaseChargesCard(props) {
    const { vendor } = props;
    const dispatch = useDispatch();
    const vendorUuid = vendor?.uuid;

    const canEdit = can(props.user.details, "billingRates");

    const savedItems = props.vendors.data.vendorBaseCharges?.items || [];

    const [enabled, setEnabled] = useState(savedItems.length > 0);
    const [form, setForm] = useState(emptyForm());
    const [currentUuid, setCurrentUuid] = useState(null);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [newRatesOpen, setNewRatesOpen] = useState(false);
    const [newRatesForm, setNewRatesForm] = useState(emptyForm());
    const [saving, setSaving] = useState(false);
    const [stopUsingOpen, setStopUsingOpen] = useState(false);
    const [removeVersionRow, setRemoveVersionRow] = useState(null);
    const [zeroConfirmOpen, setZeroConfirmOpen] = useState(false);
    const [pendingZeroAction, setPendingZeroAction] = useState(null);

    const currentRow = useMemo(
        () => getEffectiveBaseCharge(savedItems, today()),
        [savedItems]
    );

    useEffect(() => {
        if (savedItems.length > 0) {
            setEnabled(true);
        } else {
            setEnabled(false);
        }
        const effective = getEffectiveBaseCharge(savedItems, today());
        setCurrentUuid(effective?.uuid || null);
        setForm(rowToForm(effective));
    }, [savedItems]);

    const sortedHistory = useMemo(
        () =>
            [...savedItems].sort(
                (a, b) => moment(b.startDate).valueOf() - moment(a.startDate).valueOf()
            ),
        [savedItems]
    );

    const previewTotal = computeBaseTotal(form.customerCharge, form.energyChargeRate, PREVIEW_KWH);
    const previewAtZero = computeBaseTotal(form.customerCharge, form.energyChargeRate, 0);
    const savedBothZero =
        currentRow &&
        bothChargesAreZero(currentRow.customerCharge, currentRow.energyChargeRate);

    const isDirty = useMemo(() => {
        const baseline = rowToForm(currentRow);
        return (
            String(form.customerCharge) !== String(baseline.customerCharge) ||
            String(form.energyChargeRate) !== String(baseline.energyChargeRate) ||
            form.startDate !== baseline.startDate
        );
    }, [form, currentRow]);

    const handleFieldChange = (field) => (event) => {
        setForm((prev) => ({ ...prev, [field]: event.target.value }));
    };

    const reload = useCallback(() => {
        if (vendorUuid) {
            return props.getVendorBaseChargesByVendorUuid(vendorUuid, false);
        }
        return Promise.resolve();
    }, [props, vendorUuid]);

    const persistSave = useCallback(async () => {
        if (!vendorUuid) {
            return;
        }
        setSaving(true);
        const payload = {
            customerCharge: Number(form.customerCharge) || 0,
            energyChargeRate: Number(form.energyChargeRate) || 0,
            startDate: form.startDate,
            endDate: currentRow?.endDate || null,
        };
        try {
            if (currentUuid) {
                await props.updateVendorBaseCharge({ uuid: currentUuid, ...payload });
            } else {
                await props.createVendorBaseCharge(payload, vendorUuid);
            }
            dispatch(setSnackbar(true, "success", "Base charges saved."));
            await reload();
        } catch (error) {
            dispatch(setSnackbar(true, "error", "Failed to save base charges."));
        } finally {
            setSaving(false);
        }
    }, [currentUuid, currentRow, dispatch, form, props, reload, vendorUuid]);

    const persistNewRates = useCallback(async () => {
        if (!vendorUuid) {
            return;
        }
        const newStart = moment(newRatesForm.startDate);
        if (!newStart.isValid()) {
            dispatch(setSnackbar(true, "error", "Enter a valid effective date."));
            return;
        }
        setSaving(true);
        try {
            const openRow =
                savedItems.find((row) => !row.endDate) ||
                getEffectiveBaseCharge(savedItems, today());
            if (openRow?.uuid) {
                const endDate = newStart.clone().subtract(1, "day").format("YYYY-MM-DD");
                if (moment(endDate).isBefore(moment(openRow.startDate), "day")) {
                    dispatch(
                        setSnackbar(
                            true,
                            "error",
                            "New effective date must be after the current version start date."
                        )
                    );
                    setSaving(false);
                    return;
                }
                await props.updateVendorBaseCharge({ uuid: openRow.uuid, endDate });
            }
            await props.createVendorBaseCharge(
                {
                    customerCharge: Number(newRatesForm.customerCharge) || 0,
                    energyChargeRate: Number(newRatesForm.energyChargeRate) || 0,
                    startDate: newRatesForm.startDate,
                    endDate: null,
                },
                vendorUuid
            );
            dispatch(setSnackbar(true, "success", "New rate version created."));
            setNewRatesOpen(false);
            await reload();
        } catch (error) {
            dispatch(setSnackbar(true, "error", "Failed to create new rate version."));
        } finally {
            setSaving(false);
        }
    }, [dispatch, newRatesForm, props, reload, savedItems, vendorUuid]);

    const handleSave = useCallback(() => {
        if (!vendorUuid) {
            return;
        }
        if (bothChargesAreZero(form.customerCharge, form.energyChargeRate)) {
            setPendingZeroAction("save");
            setZeroConfirmOpen(true);
            return;
        }
        persistSave();
    }, [form.customerCharge, form.energyChargeRate, persistSave, vendorUuid]);

    const openNewRatesDialog = () => {
        setNewRatesForm({
            customerCharge: form.customerCharge,
            energyChargeRate: form.energyChargeRate,
            startDate: today(),
        });
        setNewRatesOpen(true);
    };

    const handleNewRatesSubmit = useCallback(() => {
        if (!vendorUuid) {
            return;
        }
        if (
            bothChargesAreZero(
                newRatesForm.customerCharge,
                newRatesForm.energyChargeRate
            )
        ) {
            setPendingZeroAction("newRates");
            setZeroConfirmOpen(true);
            return;
        }
        persistNewRates();
    }, [
        newRatesForm.customerCharge,
        newRatesForm.energyChargeRate,
        persistNewRates,
        vendorUuid,
    ]);

    const handleZeroConfirmContinue = () => {
        setZeroConfirmOpen(false);
        const action = pendingZeroAction;
        setPendingZeroAction(null);
        if (action === "save") {
            persistSave();
        } else if (action === "newRates") {
            persistNewRates();
        }
    };

    const handleZeroConfirmCancel = () => {
        setZeroConfirmOpen(false);
        setPendingZeroAction(null);
    };

    const handleToggle = (event) => {
        const on = event.target.checked;
        if (on) {
            setEnabled(true);
            if (!currentUuid && savedItems.length === 0) {
                setForm(emptyForm());
            }
            return;
        }
        if (savedItems.length > 0) {
            setStopUsingOpen(true);
            return;
        }
        setEnabled(false);
    };

    const handleStopUsingCancel = () => {
        setStopUsingOpen(false);
    };

    const handleStopUsingConfirm = useCallback(async () => {
        setSaving(true);
        try {
            await Promise.all(
                savedItems
                    .filter((row) => row?.uuid)
                    .map((row) => props.deleteVendorBaseCharge(row.uuid))
            );
            dispatch(
                setSnackbar(
                    true,
                    "success",
                    "Vendor base charges removed. Properties will use Property Utility charges."
                )
            );
            setStopUsingOpen(false);
            setHistoryOpen(false);
            setEnabled(false);
            setForm(emptyForm());
            setCurrentUuid(null);
            await reload();
        } catch (error) {
            dispatch(setSnackbar(true, "error", "Failed to remove vendor base charges."));
        } finally {
            setSaving(false);
        }
    }, [dispatch, props, reload, savedItems]);

    const handleRemoveVersionConfirm = useCallback(async () => {
        if (!removeVersionRow?.uuid) {
            return;
        }
        setSaving(true);
        try {
            await props.deleteVendorBaseCharge(removeVersionRow.uuid);
            dispatch(setSnackbar(true, "success", "Rate version removed."));
            const remainingCount = savedItems.filter(
                (row) => row.uuid !== removeVersionRow.uuid
            ).length;
            setRemoveVersionRow(null);
            if (remainingCount === 0) {
                setHistoryOpen(false);
                setEnabled(false);
                setForm(emptyForm());
                setCurrentUuid(null);
            }
            await reload();
        } catch (error) {
            dispatch(setSnackbar(true, "error", "Failed to remove rate version."));
        } finally {
            setSaving(false);
        }
    }, [dispatch, props, reload, removeVersionRow, savedItems]);

    if (!enabled && savedItems.length === 0) {
        if (!canEdit) {
            return null;
        }
        return (
            <Card elevation={3} sx={{ mb: 2 }}>
                <CardContent sx={{ p: 2 }}>
                    <FormControlLabel
                        control={<Switch checked={enabled} onChange={handleToggle} />}
                        label="This vendor uses customer/energy base charges"
                    />
                </CardContent>
            </Card>
        );
    }

    return (
        <>
            <Card elevation={3} sx={{ mb: 2 }}>
                <CardContent sx={{ p: 2 }}>
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        justifyContent="space-between"
                        alignItems={{ xs: "flex-start", sm: "center" }}
                        spacing={1}
                        sx={{ mb: 2 }}
                    >
                        <Box>
                            <Typography variant="h6">Customer and Energy charges</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Base (Distribution)
                            </Typography>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                                sx={{ mt: 0.5 }}
                            >
                                When on, these values override Property Utility for every
                                property on this vendor.
                            </Typography>
                        </Box>
                        {canEdit ? (
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={enabled}
                                        onChange={handleToggle}
                                        disabled={saving}
                                    />
                                }
                                label="Use vendor customer/energy charges"
                            />
                        ) : null}
                    </Stack>

                    <Stack
                        direction={{ xs: "column", md: "row" }}
                        spacing={2}
                        sx={{ mb: 2 }}
                        useFlexGap
                        flexWrap="wrap"
                    >
                        <TextField
                            label="Customer charge ($/mo)"
                            type="number"
                            size="small"
                            value={form.customerCharge}
                            onChange={handleFieldChange("customerCharge")}
                            disabled={!canEdit}
                            sx={{ minWidth: 160 }}
                        />
                        <TextField
                            label="Energy charge ($/kWh)"
                            type="number"
                            size="small"
                            value={form.energyChargeRate}
                            onChange={handleFieldChange("energyChargeRate")}
                            disabled={!canEdit}
                            sx={{ minWidth: 160 }}
                        />
                        <TextField
                            label="Effective from"
                            type="date"
                            size="small"
                            value={form.startDate}
                            onChange={handleFieldChange("startDate")}
                            disabled={!canEdit || !!currentUuid}
                            InputLabelProps={{ shrink: true }}
                            helperText={currentUuid ? "Use New rates from date… to change" : ""}
                            sx={{ minWidth: 160 }}
                        />
                    </Stack>

                    <Typography variant="body2" sx={{ mb: 2, fontVariantNumeric: "tabular-nums" }}>
                        Base total: <strong>${previewAtZero.toFixed(2)}</strong> at 0 kWh ·{" "}
                        <strong>${previewTotal.toFixed(2)}</strong> at {PREVIEW_KWH} kWh
                    </Typography>

                    {canEdit ? (
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            <Button
                                size="small"
                                variant="contained"
                                color="secondary"
                                startIcon={<SaveIcon />}
                                onClick={handleSave}
                                disabled={!isDirty || saving}
                            >
                                Save
                            </Button>
                            {currentUuid ? (
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<EventIcon />}
                                    onClick={openNewRatesDialog}
                                    disabled={saving}
                                >
                                    New rates from date…
                                </Button>
                            ) : null}
                            {savedItems.length > 0 ? (
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<HistoryIcon />}
                                    onClick={() => setHistoryOpen(true)}
                                >
                                    View rate history
                                </Button>
                            ) : null}
                        </Stack>
                    ) : null}
                </CardContent>
            </Card>

            <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Rate history</DialogTitle>
                <DialogContent dividers>
                    {sortedHistory.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            No saved versions.
                        </Typography>
                    ) : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Customer $</TableCell>
                                    <TableCell>Energy $/kWh</TableCell>
                                    <TableCell>Start</TableCell>
                                    <TableCell>End</TableCell>
                                    {canEdit ? <TableCell align="right">Actions</TableCell> : null}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sortedHistory.map((row) => (
                                    <TableRow
                                        key={row.uuid}
                                        selected={row.uuid === currentUuid}
                                    >
                                        <TableCell>{Number(row.customerCharge).toFixed(2)}</TableCell>
                                        <TableCell>{Number(row.energyChargeRate).toFixed(7)}</TableCell>
                                        <TableCell>{formatDate(row.startDate)}</TableCell>
                                        <TableCell>{formatDate(row.endDate)}</TableCell>
                                        {canEdit ? (
                                            <TableCell align="right">
                                                <Button
                                                    size="small"
                                                    color="error"
                                                    disabled={saving}
                                                    onClick={() => setRemoveVersionRow(row)}
                                                >
                                                    Remove
                                                </Button>
                                            </TableCell>
                                        ) : null}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setHistoryOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={newRatesOpen} onClose={() => setNewRatesOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>New rates from date</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            The current open version will end the day before the new effective date.
                        </Typography>
                        <TextField
                            label="New effective from"
                            type="date"
                            size="small"
                            value={newRatesForm.startDate}
                            onChange={(e) =>
                                setNewRatesForm((prev) => ({ ...prev, startDate: e.target.value }))
                            }
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                        />
                        <TextField
                            label="Customer charge ($/mo)"
                            type="number"
                            size="small"
                            value={newRatesForm.customerCharge}
                            onChange={(e) =>
                                setNewRatesForm((prev) => ({
                                    ...prev,
                                    customerCharge: e.target.value,
                                }))
                            }
                            fullWidth
                        />
                        <TextField
                            label="Energy charge ($/kWh)"
                            type="number"
                            size="small"
                            value={newRatesForm.energyChargeRate}
                            onChange={(e) =>
                                setNewRatesForm((prev) => ({
                                    ...prev,
                                    energyChargeRate: e.target.value,
                                }))
                            }
                            fullWidth
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setNewRatesOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleNewRatesSubmit} disabled={saving}>
                        Create version
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={stopUsingOpen} onClose={handleStopUsingCancel}>
                <DialogTitle>Stop using vendor base charges?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Billing will fall back to each property&apos;s Utility Customer Charge
                        and Energy Charge. This removes all saved customer/energy versions for
                        this vendor.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleStopUsingCancel} disabled={saving}>
                        Cancel
                    </Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={handleStopUsingConfirm}
                        disabled={saving}
                    >
                        Stop using
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={Boolean(removeVersionRow)}
                onClose={() => setRemoveVersionRow(null)}
            >
                <DialogTitle>Remove this rate version?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {removeVersionRow
                            ? `Customer $${Number(removeVersionRow.customerCharge).toFixed(2)}, energy $${Number(removeVersionRow.energyChargeRate).toFixed(7)}/kWh (${formatDate(removeVersionRow.startDate)} – ${formatDate(removeVersionRow.endDate)}).`
                            : null}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRemoveVersionRow(null)} disabled={saving}>
                        Cancel
                    </Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={handleRemoveVersionConfirm}
                        disabled={saving}
                    >
                        Remove
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={zeroConfirmOpen} onClose={handleZeroConfirmCancel}>
                <DialogTitle>Both charges are 0</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Billing will use $0 (not property rates). Continue?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleZeroConfirmCancel}>Cancel</Button>
                    <Button
                        color="warning"
                        variant="contained"
                        onClick={handleZeroConfirmContinue}
                    >
                        Continue
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

VendorBaseChargesCard.propTypes = {
    vendor: PropTypes.object.isRequired,
    user: PropTypes.object.isRequired,
    vendors: PropTypes.object.isRequired,
    createVendorBaseCharge: PropTypes.func.isRequired,
    updateVendorBaseCharge: PropTypes.func.isRequired,
    deleteVendorBaseCharge: PropTypes.func.isRequired,
    getVendorBaseChargesByVendorUuid: PropTypes.func.isRequired,
};

function State(state) {
    return {
        user: state.user,
        vendors: state.vendors,
    };
}

export default connect(State, {
    createVendorBaseCharge,
    updateVendorBaseCharge,
    deleteVendorBaseCharge,
    getVendorBaseChargesByVendorUuid,
})(VendorBaseChargesCard);
